import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import {
    columnsCompanyInfoOS,
    CompanyDataAll,
    flattenSectorProductsDomain,
    fetchCompanyOS,
    fetchProjectByIdService,
    getCompanyPayload,
} from "services";

// Types
import { TBriefData, projectStore } from "./ProjectSlice.types";
import { resetSearchInfo } from "../../actions";
import { ProjectDataResponse } from "services/projects/project.types";
import { customAsyncThunk } from "store/slice/slices.functions";

export const initialBriefData = {
    position: null,
    roleFunction: "" as const,
    roleFunctions: [],
    roleLevel: "" as const,
    roleLevels: [],
    chart1: { x: undefined, y: undefined, radius: undefined },
    chart2: { x: undefined, y: undefined, radius: undefined },
    facets: {
        situational: [],
        domain: [],
        functional: [],
    },
};

const initialState: projectStore = {
    company: {
        data: {} as ReturnType<typeof flattenSectorProductsDomain>,
        status: "pristine",
    },
    briefData: initialBriefData,
    data: {} as ProjectDataResponse,
    status: "pristine",
};

// Thunks
export const fetchProjectById = createAsyncThunk<ProjectDataResponse, { projectId: number }>(
    "project/fetchProjectById",
    async ({ projectId }) => {
        const { data } = await fetchProjectByIdService<ProjectDataResponse>(Number(projectId), undefined, {
            include_avatars: true,
            include_brief_counts: true,
        });
        return data;
    }
);

export const fetchProjectCompany = customAsyncThunk<ReturnType<typeof flattenSectorProductsDomain>, string | number>(
    "project/fetchCompanyData",
    async (companyId) => {
        const params = getCompanyPayload({
            termField: "id",
            termFilter: `${companyId}`,
            columns: columnsCompanyInfoOS,
        });

        const { data } = await fetchCompanyOS<CompanyDataAll>(params);
        const convertedCompanyData = flattenSectorProductsDomain(data.results[0]);
        return convertedCompanyData;
    }
);

// Reducer
export const projectSlice = createSlice({
    name: "project/data",
    initialState: initialState,
    reducers: {
        resetProjectData: () => initialState,
        cleanProjectData: (state) => ({
            ...initialState,
            company: state.company,
        }),
        setBriefData: (state, action: PayloadAction<TBriefData>) => ({
            ...state,
            briefData: action.payload,
        }),
        resetBriefData: (state) => ({
            ...state,
            briefData: initialBriefData,
        }),
    },
    extraReducers: (builder) => {
        // Company
        builder.addCase(fetchProjectCompany.pending, (state) => ({
            ...state,
            company: {
                ...state.company,
                status: "fetching",
            },
        }));
        builder.addCase(fetchProjectCompany.fulfilled, (state, action) => ({
            ...state,
            company: {
                data: action.payload,
                status: "done",
            },
        }));
        builder.addCase(fetchProjectCompany.rejected, (state) => ({
            ...state,
            company: {
                ...state.company,
                status: "error",
            },
        }));
        // Project
        builder.addCase(fetchProjectById.pending, (state) => ({
            ...state,
            status: "fetching",
        }));
        builder.addCase(fetchProjectById.fulfilled, (state, action) => ({
            ...state,
            status: "done",
            data: action.payload,
        }));
        builder.addCase(fetchProjectById.rejected, (state) => ({
            ...state,
            status: "error",
        }));
        builder.addCase(resetSearchInfo, () => initialState);
    },
});

// Actions
export const { resetProjectData, cleanProjectData, setBriefData, resetBriefData } = projectSlice.actions;

// Selectors
export const getProject = (store: RootState) => store.project.data;
export const getProjectCompanyData = (store: RootState) => store.project.data.company;
export const getBriefData = (store: RootState) => store.project.data.briefData;

export default projectSlice.reducer;
